// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as TagsActions from "features/tags/actions"
import { FavouriteTag, CreateFavouriteTagRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type CreateFavouriteTagRpc = (request: Init<typeof CreateFavouriteTagRequestSchema>) => Observable<FavouriteTag>

export const createFavouriteTag = (createFavouriteTagRpc: CreateFavouriteTagRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createFavouriteTag.match),
      mergeMap(action =>
        of(action.payload)
          .pipe(
            mergeMap(createFavouriteTagRpc),
            map(t => t),
            map(Slice.createFavouriteTagFulfilled),
            catchError(handleError),
          )
      ),
    )

const createFavouriteTagRpc = (request: Init<typeof CreateFavouriteTagRequestSchema>): Observable<FavouriteTag> =>
  executorMutation(() => tagService.createFavouriteTag(create(CreateFavouriteTagRequestSchema, request)))

const handleError = (e: unknown) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_favourite_tag_not_found", message: "Tag not found." }),
          messageType: "ERROR",
        })),
        // fetch recent tags to refresh list
        of(TagsActions.fetchRecentTags())
      )
    }
  }

  return of(
    showSnackBar({
      message: t({ id: "msg_favourite_tag_failed", message: "Failed to favourite tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    })
  )
}

export default createFavouriteTag(createFavouriteTagRpc)
